import React from "react";
import {
  Dropdown,
  DropdownButton,
  Nav,
  Navbar,
  NavDropdown,
  OverlayTrigger,
  Tooltip,
  Image,
  ButtonGroup,
  Button,
  Badge,
} from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import { useLocation, NavLink } from "react-router-dom";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import i18next from "i18next";
import { AppState, LogoutOptions, RedirectLoginOptions, User } from "@auth0/auth0-react";
import { saveAs } from "file-saver";
import * as htmlToImage from "html-to-image";
import { shallowEqual } from "react-redux";

import LogoContainer from "./LogoContainer";
import {
  allowableEarly,
  allowableLate,
  configAgencyName,
  contentOnlyForTA,
  defaultLanguage,
  downloadableStopsData,
  initialTableWidth,
  showChangeLanguage,
} from "../../config";
import {
  dispatchEarlyLateSettings,
  dispatchVehicleId,
  resetBusLocationsByDate,
  resetDataActiveBus,
  resetLinesShapeStops,
  setActiveBus,
  setDeviationReportMapData,
  setLoadingTable,
  setRouteMapRange,
} from "../../redux/actions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import CurrentTimeDiv from "./CurrentTimeDiv";
import AuthComponent from "./AuthComponent";
import polishFlag from "../../assets/flags/polishFlag.svg";
import ukFlag from "../../assets/flags/ukFlag.svg";
import newTabIcon from "../../assets/icons/newTabIcon.svg";
import downloadIcon from "../../assets/icons/downloadIcon.svg";
import mapDownloadIcon from "../../assets/icons/mapDownloadIcon.svg";
import SettingsModal from "./SettingsModal";
import { stopInfoResetPart } from "../../data/constants";
import { AgencyContext } from "../../hooks/AgencyContext";
import { valueFromLocalStorage } from "../../utils/helpers";

const HeaderContainer = styled.div`
  position: relative;
  margin-left: 0px;
  margin-right: 0px;
  width: 100%;
  height: 58px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
`;

const LanguageContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  gap: 0.5rem;
`;

//* Menu was modified and differs from directories tree!
const Header = ({
  setTableWidth,
  setLoading,
  loginWithRedirect,
  isAuthenticated,
  user,
  logout,
  isLoading,
}: {
  setTableWidth: (arg0: number) => void;
  setLoading: (arg1: boolean) => void;
  loginWithRedirect: (options?: RedirectLoginOptions<AppState> | undefined) => Promise<void>;
  isAuthenticated: boolean;
  user: User;
  logout: (options?: LogoutOptions | undefined) => void;
  isLoading: boolean;
}): JSX.Element => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const currentPath = location?.pathname;

  const dispatch: Dispatch = useAppDispatch();

  const [isMapReady, startWithAccordion]: [boolean, boolean] = useAppSelector(
    (state: RootState) => [state?.constantValues?.isMapReady, state?.constantValues?.startWithAccordion],
    shallowEqual
  );
  // console.log("startWithAccordion:", startWithAccordion);

  //* AgencyContext
  const { initialMapRange, initialMapCenter } = React.useContext(AgencyContext) as AgencyContextType;

  const [activeLanguage, setActiveLanguage] = React.useState<string>(localStorage.getItem("i18nextLng" as string) || "en");
  const [isOpenReportsDropdown, setIsOpenReportsDropdown] = React.useState<boolean>(false);
  const [allowableLateRedux, setAllowableLateRedux] = React.useState<number>(
    valueFromLocalStorage("allowableLateRedux")
      ? JSON.parse(valueFromLocalStorage("allowableLateRedux") as string)
      : allowableLate
  ); //* [min]
  const [allowableEarlyRedux, setAllowableEarlyRedux] = React.useState<number>(
    valueFromLocalStorage("allowableEarlyRedux")
      ? JSON.parse(valueFromLocalStorage("allowableEarlyRedux") as string)
      : allowableEarly
  ); //* [min]
  // console.log("allowableLateRedux, allowableEarlyRedux", allowableLateRedux, allowableEarlyRedux);

  //* Save early/ late time settings in LocalStorage
  React.useEffect(() => {
    const earlyLateObj = {
      allowableLateRedux,
      allowableEarlyRedux,
    };

    Object.entries(earlyLateObj).forEach((entry: [string, number]) =>
      localStorage.setItem(entry[0], JSON.stringify(entry[1]))
    );
    dispatch(dispatchEarlyLateSettings(earlyLateObj));
  }, [allowableLateRedux, allowableEarlyRedux, dispatch]);

  // Reset language
  React.useEffect(() => {
    if (localStorage.getItem("i18nextLng")?.length! > 2) {
      i18next.changeLanguage("en");
    }
  }, []);

  // Change language
  const handleLanguageChange = (event: string | null): void => {
    // console.log({ event });
    // console.log("localStorage.getItem('i18nextLng'):", localStorage.getItem("i18nextLng"));
    i18n.changeLanguage(event as string);
    setActiveLanguage(event as string);
  };

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    currentPath === "/"
      ? (setTableWidth(initialTableWidth), dispatch(setLoadingTable(true)), setLoading(true))
      : currentPath.includes("/bus") || currentPath.includes("/routesInfo") || currentPath.includes("/modifyTripStatus")
        ? (setTableWidth(4), dispatch(setLoadingTable(true)), setLoading(true))
        : (setTableWidth(6), dispatch(setLoadingTable(true)), setLoading(true));
  }, [currentPath, dispatch, setLoading, setTableWidth]);

  //* DeviationReportMap reset + restore initial state
  const onReturnClick = (): void => {
    setTimeout(async () => {
      await dispatch(setRouteMapRange(initialMapRange!));
      await dispatch(setActiveBus(""));
      await dispatch(resetLinesShapeStops());
      await dispatch(resetDataActiveBus());
      await dispatch(
        setDeviationReportMapData({
          ...stopInfoResetPart,
          loc: { lat: initialMapCenter?.lat!, lon: initialMapCenter?.lng! },
        })
      );
      await dispatch(resetBusLocationsByDate());
      await dispatch(dispatchVehicleId(""));
      await localStorage.removeItem("deviationTableActiveTr");
    }, 800);
  };

  //* General NavBar Tooltip
  const NavTooltip = ({
    id,
    children,
    title,
    placement,
  }: {
    id: string;
    children: JSX.Element;
    title: React.ReactNode;
    placement: Placement;
  }): JSX.Element => {
    return (
      <OverlayTrigger overlay={<Tooltip id={id}>{title}</Tooltip>} placement={placement} trigger={["focus", "hover"]}>
        {children}
      </OverlayTrigger>
    );
  };

  // Select Language Dropdown
  const SelectLanguage: React.FC = (): JSX.Element => {
    return (
      <DropdownButton
        // Title as JSX.Element
        title={
          <React.Fragment>
            {activeLanguage === "en" ? (
              <LanguageContainer>
                <Image src={ukFlag} roundedCircle={true} alt="UK Flag" height="22" width="22" /> EN
              </LanguageContainer>
            ) : (
              <LanguageContainer>
                <Image src={polishFlag} roundedCircle={true} alt="Polish Flag" height="22" width="22" /> PL
              </LanguageContainer>
            )}
          </React.Fragment>
        }
        onSelect={handleLanguageChange}
        variant="secondary"
        menuVariant="dark"
        align="start"
        size="sm"
      >
        <Dropdown.Item eventKey="pl" active={activeLanguage === "pl" ? true : false}>
          <LanguageContainer>
            <Image src={polishFlag} roundedCircle={true} alt="Polish Flag" height="22" />
            Polski
          </LanguageContainer>
        </Dropdown.Item>
        <Dropdown.Item eventKey="en" active={activeLanguage === "en" || activeLanguage === "en-GB" ? true : false}>
          <LanguageContainer>
            <Image src={ukFlag} roundedCircle={true} alt="UK Flag" height="22" width="22" />
            English
          </LanguageContainer>
        </Dropdown.Item>
      </DropdownButton>
    );
  };

  //* Download Map to .png file
  const downloadMapOnClick = React.useCallback((): void => {
    if (!isMapReady) return;
    if (isMapReady) {
      const mapContainer = document.getElementById("mapContainer") as HTMLDivElement;
      // console.log("mapContainer:", mapContainer);

      htmlToImage
        .toPng(mapContainer, { cacheBust: true })
        .then((dataUrl) => {
          // console.log("dataUrl", typeof dataUrl, dataUrl);
          saveAs(dataUrl!, `${t("Mapa")}.png`);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [isMapReady, t]);

  return (
    <React.Fragment>
      <Navbar bg="secondary" variant="dark">
        {/* //* Header NavBrand (separate tooltip) */}
        <HeaderContainer>
          <OverlayTrigger
            placement={"bottom"}
            overlay={
              <Tooltip id={"headerTooltip-NavBrand"}>
                <span className="span_warning span_bold">
                  {startWithAccordion ? t("Live Blocks (accordion)") : `${t("On-time Performance")} (${t("główna tabela")})`}
                </span>
                <br />
                <Trans t={t}>Wróć na stronę główną i przywróć mapę początkową</Trans>,
                <br />
                {t("Odznacz pojazd")}
                <br />
                {t("Odznacz trasę i przystanki")}
              </Tooltip>
            }
          >
            <Navbar.Brand
              as={NavLink}
              // to="/"
              to={startWithAccordion ? "/views/accordion" : "/"}
              onClick={onReturnClick}
              style={{ pointerEvents: initialMapRange && initialMapCenter ? undefined : "none" }}
            >
              <LogoContainer />
            </Navbar.Brand>
          </OverlayTrigger>

          {/* //@ Header Navigation */}
          <Nav className="ms-0">
            {/* //* 1. Live dropdown */}
            <NavDropdown title={t("Live")} id="Dropdown-live">
              <Dropdown.ItemText className="text-muted small">{t("General Information")}</Dropdown.ItemText>
              <NavTooltip
                id={"navigate-to-OTP_01"}
                title={`${t("On-time Performance")} (${t("główna tabela")})`}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/" as={NavLink} onClick={onReturnClick}>
                    {t("On-time Performance")}
                  </NavDropdown.Item>
                }
              />
              {/* //^ On-Time Performance Table V2 */}
              {process.env.NODE_ENV === "development" ? (
                <NavTooltip
                  id={"navigate-to-OTP_01_V2"}
                  title={`${t("On-time Performance")} (${t("główna tabela")}) V2`}
                  placement={"right"}
                  children={
                    <NavDropdown.Item
                      to="tableV2/onTimePerformance"
                      as={NavLink}
                      onClick={onReturnClick}
                      className="navigation-item"
                    >
                      {`${t("On-time Performance")} V2`}{" "}
                      <Badge pill={true} bg="danger">
                        {"Beta"}
                      </Badge>
                    </NavDropdown.Item>
                  }
                />
              ) : null}
              {/* //^ HeadwayPerformanceTable -> for TA - MiamiBeach only */}
              {configAgencyName === ("MiamiBeach" as string) && contentOnlyForTA ? (
                <NavTooltip
                  title={t("Wydajność odstępu czasu")}
                  id={"navigate-to-headway_02"}
                  placement={"right"}
                  children={
                    <NavDropdown.Item to="/headwayPerformance" as={NavLink}>
                      {t("Wydajność odstępu czasu")}
                    </NavDropdown.Item>
                  }
                />
              ) : null}
              <NavTooltip
                title={t("Live Blocks (accordion)")}
                id={"navigate-to-accordion_03"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/views/accordion" as={NavLink}>
                    {t("Live Blocks (accordion)")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Synoptic Live Ladder View")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-synoptic_04"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/synopticLiveLadderView"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Synoptic Live Ladder View")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Other information")}</Dropdown.ItemText>
              <NavTooltip
                title={t("Status wybranego pojazdu")}
                id={"navigate-to-bus-status_05"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/views/bus" as={NavLink}>
                    {t("Status pojazdu")}
                  </NavDropdown.Item>
                }
              />
              {defaultLanguage === ("pl" as string) ? (
                <NavTooltip
                  title={
                    <React.Fragment>
                      {t("Uprawnienia kierowcy")}
                      <br />
                      {t("Otwiera się w nowej zakładce")}
                    </React.Fragment>
                  }
                  id={"navigate-to-driverInfo_06"}
                  placement={"right"}
                  children={
                    <NavDropdown.Item
                      href="https://moj.gov.pl/uslugi/engine/ng/index?xFormsAppName=UprawnieniaKierowcow&xFormsOrigin=EXTERNAL"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="navigation-item"
                    >
                      {t("Uprawnienia kierowcy")}
                      <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                    </NavDropdown.Item>
                  }
                />
              ) : null}

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">GTFS - RT</Dropdown.ItemText>
              <NavTooltip
                title={t("Modyfikuj status kursu")}
                id={"navigate-to-accordion_07"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/views/modifyTripStatus" as={NavLink}>
                    {t("Modyfikuj status kursu")}
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">Cache</Dropdown.ItemText>
              <NavTooltip
                title={t("Usuń cache pojazdu/pojazdów")}
                id={"navigate-to-cache_08"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/deleteVehiclesCache" as={NavLink}>
                    {t("Usuń cache pojazdu/pojazdów")}
                  </NavDropdown.Item>
                }
              />
            </NavDropdown>

            {/* //* 2. Alerts dropdown */}
            <NavDropdown title={t("Alerts")} id="Dropdown-alerts">
              <Dropdown.ItemText className="text-muted small">{t("Service Alerts Manager")}</Dropdown.ItemText>
              <NavTooltip
                title={`${t("List Service Alerts")}, (${t("takes up the entire window")})`}
                id={"navigate-to-alerts_11"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/alerts"
                    as={NavLink}
                    // end={true}
                  >
                    {t("List Service Alerts")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={`${t("Add a New Service Alert")}, (${t("takes up the entire window")})`}
                id={"navigate-to-new-alert_12"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/alerts/new" as={NavLink}>
                    {t("Add a New Service Alert")}
                  </NavDropdown.Item>
                }
              />
            </NavDropdown>

            {/* //* 3. Reports dropdown*/}
            <NavDropdown
              title={t("Raporty")}
              id="Dropdown-reports"
              show={isOpenReportsDropdown}
              onToggle={(open: boolean) => setIsOpenReportsDropdown(open)}
            >
              <Dropdown.ItemText className="text-muted small">{t("Raporty AVL")}</Dropdown.ItemText>
              <NavTooltip
                title={t("Pojedynczy raport AVL")}
                id={"navigate-to-AVL_21"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/singleAVL" as={NavLink}>
                    {t("Pojedynczy raport AVL")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Raport AVL dla wszystkich pojazdów")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-AVL_22"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/AllVehiclesAVLReport"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Raport AVL dla wszystkich pojazdów")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Ostatnie 24h")}
                id={"navigate-to-last24hours_23"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/last24hours" as={NavLink}>
                    {t("Ostatnie 24h")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Pojazdy na mapie - migawka")}
                id={"bus-location-by-date_24"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/locationByDate" as={NavLink}>
                    {t("Pojazdy na mapie - migawka")}
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Raporty punktualności")}</Dropdown.ItemText>
              <NavTooltip
                title={t("Raport punktualności wg linii")}
                id={"navigate-to-deviation_25"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/deviation" as={NavLink}>
                    {t("Raport punktualności wg linii")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Schedule Adherence for all Routes")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-all-routes_26"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/scheduleAdherenceAllRoutes"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Schedule Adherence for all Routes")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Raport punktualności wg kursu")}
                id={"navigate-to-single-trip_27"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/singleTrip" as={NavLink}>
                    {t("Raport punktualności wg kursu")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Raport punktualności wszystkich kursów")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-all-trips_28"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/allTrips" as={NavLink} target="_blank" className="navigation-item">
                    {t("Raport punktualności wszystkich kursów")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Raport punktualności dla przystanku")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-single-stop_29"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/stopPunctualityReport"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Raport punktualności dla przystanku")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Download Average Speeds Report")}
                id={"navigate-to-average_speeds_30"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/downloads/averageSpeeds" as={NavLink} className="navigation-item">
                    {t("Raport o średnich prędkościach")}
                    <img
                      src={downloadIcon}
                      alt="Download icon"
                      width={20}
                      height={20}
                      style={{
                        filter: currentPath.includes("/averageSpeeds") ? "none" : "brightness(25%)",
                      }}
                    />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Raport punktualności i zapełnienia wg zadania z kursami")}
                id={"navigate-to-block-with-trips_31"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/blockWithTripsPunctualityAndOccupancyReport" as={NavLink}>
                    {t("Raport punktualności i zapełnienia wg zadania z kursami")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Raport średniej punktualności i zapełnienia dla kursu")}
                id={"navigate-to-average_trip_32"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/reports/tripAveragePaxReport" as={NavLink}>
                    {t("Raport średniej punktualności i zapełnienia dla kursu")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Raport czasu postoju")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-single-stop_33"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/dwellTimeReport" as={NavLink} target="_blank" className="navigation-item">
                    {t("Raport czasu postoju")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("RouteTripsStopAdherence")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"routeTripsStopAdherence_34"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/routeTripsStopAdherence"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("RouteTripsStopAdherence")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />

              {/* //* Stats Charts: opens in a new window */}
              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Statystyki - dokładność prognoz")}</Dropdown.ItemText>
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Wykres przedziału dokładności prognozy")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-chart01_35"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/predictionAccuracyInterval"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Wykres przedziału dokładności prognozy")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Wykres punktowy dokładności przewidywania")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-chart02_36"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/predictionAccuracyScatter"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Wykres punktowy dokładności przewidywania")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Wykres zakresu dokładności przewidywań")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-chart03_37"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/predictionAccuracyRange"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Wykres zakresu dokładności przewidywań")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Downloads Section")}</Dropdown.ItemText>
              <NavTooltip
                title={t("Download all Vehicles AVL Data")}
                id={"navigate-to-download-data_38"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/downloads/downloadableContentAVL"
                    as={NavLink}
                    className="navigation-item download_section"
                  >
                    {t("Download all Vehicles AVL Data")}
                    <img
                      src={downloadIcon}
                      alt="Download icon"
                      width={20}
                      height={20}
                      style={{
                        filter: currentPath.includes("/downloadableContentAVL") ? "none" : "brightness(25%)",
                      }}
                    />
                  </NavDropdown.Item>
                }
              />
              {/* //^ For PKS only! */}
              {downloadableStopsData ? (
                <NavTooltip
                  title={<React.Fragment>{t("31-dniowy raport punktualności dla wszystkich przystanków")}</React.Fragment>}
                  id={"navigate-to-all-stops_39"}
                  placement={"right"}
                  children={
                    <NavDropdown.Item
                      to="/downloads/allStopsPunctualityReportDownLoad"
                      as={NavLink}
                      className="navigation-item download_section"
                    >
                      {t("Raport punktualności dla wszystkich przystanków")}
                      <img
                        src={downloadIcon}
                        alt="Download icon"
                        width={20}
                        height={20}
                        style={{
                          filter: currentPath.includes("/allStopsPunctualityReportDownLoad") ? "none" : "brightness(25%)",
                        }}
                      />
                    </NavDropdown.Item>
                  }
                />
              ) : null}
              <NavTooltip
                title={t("Pobierz dzienny raport przestrzegania harmonogramu przystanków")}
                id={"navigate-to-downloadDailyStopsAdherence_40"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/downloads/downloadDailyStopsAdherence"
                    as={NavLink}
                    className="navigation-item download_section"
                  >
                    {t("Pobierz 'codzienne przystanki - przestrzeganie harmonogramu'")}
                    <img
                      src={downloadIcon}
                      alt="Download icon"
                      width={20}
                      height={20}
                      style={{
                        filter: currentPath.includes("/downloadDailyStopsAdherence") ? "none" : "brightness(25%)",
                      }}
                    />
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Inne raporty")}</Dropdown.ItemText>
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Raport przypisań pojazdów do bloku")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-vehicleToBlock_41"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/vehicleToBlockReport"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Raport przypisań pojazdów do bloku")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Raport ukończonych kursów")}
                id={"navigate_to_completed_trips_42"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/completedTripsReport" as={NavLink}>
                    {t("Raport ukończonych kursów")}
                  </NavDropdown.Item>
                }
              />
            </NavDropdown>

            {/* //* 4. Routes & Schedules dropdown*/}
            <NavDropdown title={t("Routes & Schedules")} id="Dropdown-Routes&Schedules">
              <Dropdown.ItemText className="text-muted small">{t("Trasy")}</Dropdown.ItemText>
              <NavTooltip
                title={t("Trasy i sieć")}
                id={"navigate-to-linesInfo_101"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/views/routesInfo" as={NavLink}>
                    {t("Trasy i sieć")}
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={t("Trip Info")}
                id={"navigate-to-linesInfo_102"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/views/tripInfo" as={NavLink}>
                    {t("Trip Info")}
                  </NavDropdown.Item>
                }
              />

              <Dropdown.Divider />
              <Dropdown.ItemText className="text-muted small">{t("Rozkłady")}</Dropdown.ItemText>
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Rozkład jazdy wg linii")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-routes-schedule_103"}
                placement={"right"}
                children={
                  <NavDropdown.Item to="/window/routeSchedule" as={NavLink} target="_blank" className="navigation-item">
                    {t("Rozkład jazdy wg linii")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              <NavTooltip
                title={
                  <React.Fragment>
                    {t("Harmonogramy blokowe")}
                    <br />
                    {t("Otwiera się w nowej zakładce")}
                  </React.Fragment>
                }
                id={"navigate-to-brigade-schedule_104"}
                placement={"right"}
                children={
                  <NavDropdown.Item
                    to="/window/brigadeScheduleReport"
                    as={NavLink}
                    target="_blank"
                    className="navigation-item"
                  >
                    {t("Harmonogramy blokowe")}
                    <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                  </NavDropdown.Item>
                }
              />
              {/* //^ For TA only! */}
              {contentOnlyForTA ? (
                <NavTooltip
                  title={
                    <React.Fragment>
                      {t("Compare Data TA Scheduler and TC Scheduler")}
                      <br />
                      {t("Otwiera się w nowej zakładce")}
                    </React.Fragment>
                  }
                  id={"navigate-to-bus-compare-data_105"}
                  placement={"right"}
                  children={
                    <NavDropdown.Item to="/window/compareApisData" as={NavLink} target="_blank" className="navigation-item">
                      {t("Compare Data TA Scheduler and TC Scheduler")}
                      <img src={newTabIcon} alt="New window icon" width={20} height={20} />
                    </NavDropdown.Item>
                  }
                />
              ) : null}
            </NavDropdown>
          </Nav>

          {/* //* CurrentTimeDiv */}
          <Nav.Item className="ms-auto me-auto text-white lead text-center">
            <CurrentTimeDiv />
          </Nav.Item>

          {/* //* Select Language */}
          {showChangeLanguage ? (
            <Nav.Item className="justify-content-end" style={{ marginRight: "1.2rem" }}>
              <SelectLanguage />
            </Nav.Item>
          ) : null}

          {/* //* Auth, Settings */}
          <ButtonGroup>
            <AuthComponent
              loginWithRedirect={loginWithRedirect}
              user={user}
              logout={logout}
              isLoading={isLoading}
              isAuthenticated={isAuthenticated}
            />
            {/* //* Settings Modal */}
            {isAuthenticated ? (
              <OverlayTrigger
                placement={"bottom"}
                overlay={<Tooltip id={"headerTooltipDownlandMap"}>{t("Pobierz mapę (.png)")}</Tooltip>}
              >
                <Button
                  variant="danger"
                  size="sm"
                  onClick={downloadMapOnClick}
                  disabled={isMapReady && !isLoading ? false : true}
                >
                  <img src={mapDownloadIcon} alt="Map Download Icon" width={18} height={18} />
                </Button>
              </OverlayTrigger>
            ) : null}
            {isAuthenticated ? (
              <SettingsModal
                allowableLateRedux={allowableLateRedux}
                allowableEarlyRedux={allowableEarlyRedux}
                setAllowableLateRedux={setAllowableLateRedux}
                setAllowableEarlyRedux={setAllowableEarlyRedux}
              />
            ) : null}
          </ButtonGroup>
        </HeaderContainer>
      </Navbar>
    </React.Fragment>
  );
};

export default Header;
